import { Component, effect, inject, input, output, signal } from '@angular/core';
import { AbstractControl, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { faCircleCheck } from '@fortawesome/free-solid-svg-icons';
import { faCircleInfo, faComment, faLaptopBinary, faTrashCan, faCircleExclamation } from '@fortawesome/pro-regular-svg-icons';

import { ConfirmDialogComponent } from '@eiq/ui';
import { QuestionType } from '@empathy/survey/base';

import { ExtendedQuestionType } from './../../models/notepad-question-type.constant';
import { NotepadQuestion } from '../../models/notepad-model.model';
import { NotepadQuestionExcludedRender } from '../../models/notepad-question-not-render.constant';
import { NotepadQuestionTypeConfig } from '../../models/notepad-question-type-config.constant';
import { NotepadQuestionTypeFormComponent } from '../../models/notepad-question-type-form-component.constant';
import { NotepadService } from '../../services/notepad.service';

@Component({
  selector: 'empathy-question-form',
  templateUrl: './question-form.component.html',
  styleUrl: './question-form.component.scss'
})
export class QuestionFormComponent {
  commentCount = input<number>();
  question = input<AbstractControl>();
  readonly = input(false);
  canComment = input(false);

  addComment = output<string>();
  delete = output();
  filterComments = output<string>();
  update = output<FormGroup>();

  showChangeQuestionType$ = signal(false);

  faCheck = faCircleCheck;
  faCircleExclamation = faCircleExclamation;
  faCircleInfo = faCircleInfo;
  faComment = faComment;
  faLaptopBinary = faLaptopBinary;
  faTrashCan = faTrashCan;

  questionForm: FormGroup;
  questionTypeFormComponent = NotepadQuestionTypeFormComponent;
  questionTypeConfig = NotepadQuestionTypeConfig;
  notepadQuestionExcludedRender = NotepadQuestionExcludedRender;

  private dialog = inject(MatDialog);
  private notepadService = inject(NotepadService);

  constructor() {
    effect(() => {
      if (this.question()) {
        this.questionForm = this.question() as FormGroup;
      }
    });
  }

  changeQuestionType(type: ExtendedQuestionType): void {
    this.question().get('questionType').patchValue(type);

    const currentQuestionForm = this.questionForm;
    const questionId = currentQuestionForm.get('questionId').value;

    const newQuestion: NotepadQuestion = this.questionTypeConfig[type].factory(questionId, type);

    const newQuestionForm = this.notepadService.getQuestionForm(newQuestion);

    newQuestionForm.get('questionText').patchValue(currentQuestionForm.get('questionText').value);

    if (newQuestion.questionType !== QuestionType.InfoPage) {
      newQuestionForm.get('mandatoryInd').patchValue(currentQuestionForm.get('mandatoryInd').value);
    }

    const radioQuestions = [QuestionType.SingleChoice, QuestionType.MultiChoice, QuestionType.Dropdown] as ExtendedQuestionType[];
    if (radioQuestions.includes(newQuestion.questionType)) {
      newQuestionForm.get('otherOptionInd').patchValue(currentQuestionForm.get('otherOptionInd').value);
      newQuestionForm.get('randomizeOptionsInd').patchValue(currentQuestionForm.get('randomizeOptionsInd').value);
    }

    if (newQuestion.questionType === QuestionType.RankSort) {
      newQuestionForm.get('noneOptionInd').patchValue(currentQuestionForm.get('noneOptionInd').value);
    }

    newQuestionForm.get('cards')?.patchValue(currentQuestionForm.get('cards').value);
    newQuestionForm.get('options')?.patchValue(currentQuestionForm.get('options').value);

    newQuestionForm.get('instructions').patchValue(currentQuestionForm.get('instructions').value);
    newQuestionForm.get('programmerNotes').patchValue(currentQuestionForm.get('programmerNotes').value);

    this.update.emit(newQuestionForm);
    this.closeAddQuestionOverlay();
  }

  onDeleteQuestion(): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Delete',
        confirmText: 'Are you sure you want to delete question?',
        confirmBtn: 'Confirm',
        color: 'warn'
      }
    });

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (!confirmed) {
        return;
      }

      this.delete.emit();
    });
  }

  getQuestionType(): QuestionType {
    return this.questionForm.value.questionType as QuestionType;
  }

  showQuestionTypeSelect(): void {
    this.showChangeQuestionType$.set(true);
  }

  closeAddQuestionOverlay(): void {
    this.showChangeQuestionType$.set(false);
  }
}
